import { FC, useRef, useEffect, useCallback, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import * as echarts from 'echarts';
import { FuelConsumptionResponse } from './types';
import ChartErrorState from './ChartErrorState';
import { fuelAxisUnit } from './fuelAxisUnit';
import { formatCompact, formatDecimal, monthAbbreviations } from '../../utils/localeFormat';
import { fuelColors, fallbackColors, chartChrome } from '../../utils/chartColors';
import './FuelConsumptionChart.scss';

interface FuelConsumptionChartProps {
  data: FuelConsumptionResponse | null;
  isLoading: boolean;
  error?: Error | null;
  onRetry?: () => void;
}


const FuelConsumptionChart: FC<FuelConsumptionChartProps> = ({ data, isLoading, error, onRetry }) => {
  const { t, i18n } = useTranslation();
  // optional: formatters fall back to the i18next fallbackLng when absent,
  // which must not become a crash if useTranslation yields no i18n
  const lng = i18n?.language;
  // memoised so it can be an effect dependency without re-firing every render
  const monthNames = useMemo(() => monthAbbreviations(lng), [lng]);
  const chartInstance = useRef<echarts.ECharts | null>(null);
  const [chartNode, setChartNode] = useState<HTMLDivElement | null>(null);

  const chartCallbackRef = useCallback((node: HTMLDivElement | null) => {
    setChartNode(node);
  }, []);

  const pivotedData = useMemo(() => {
    if (!data || data.monthly.length === 0) return null;

    const monthsSet = new Set<string>();
    const fuelTypesSet = new Set<string>();
    const dataMap = new Map<string, Map<string, number>>();

    data.monthly.forEach((item) => {
      monthsSet.add(item.month);
      fuelTypesSet.add(item.fuelType);
      if (!dataMap.has(item.month)) dataMap.set(item.month, new Map());
      const monthMap = dataMap.get(item.month);
      if (monthMap) monthMap.set(item.fuelType, item.quantity);
    });

    const months = Array.from(monthsSet).sort();
    const fuelTypes = Array.from(fuelTypesSet);

    return { months, fuelTypes, dataMap };
  }, [data]);

  useEffect(() => {
    if (!chartNode || !pivotedData) {
      return;
    }

    if (!chartInstance.current || chartInstance.current.getDom() !== chartNode) {
      chartInstance.current?.dispose();
      chartInstance.current = echarts.init(chartNode);
    }

    const { months, fuelTypes, dataMap } = pivotedData;

    const monthLabels = months.map((m) => {
      const monthIndex = parseInt(m.split('-')[1], 10) - 1;
      return monthNames[monthIndex] || m;
    });

    const fuelMap = fuelColors();
    const fallback = fallbackColors();
    const chrome = chartChrome();
    const axisUnit = fuelAxisUnit(data?.summary ?? [], fuelTypes);

    let fallbackIdx = 0;
    const series: echarts.BarSeriesOption[] = fuelTypes.map((fuel) => {
      let color = fuelMap[fuel];
      if (!color) {
        color = fallback[fallbackIdx % fallback.length];
        fallbackIdx += 1;
      }
      return {
        type: 'bar',
        name: fuel,
        data: months.map((m) => dataMap.get(m)?.get(fuel) ?? 0),
        itemStyle: { color },
        barMaxWidth: 40,
      };
    });

    const option: echarts.EChartsOption = {
      /**
       * valueFormatter, NOT a bare `trigger: 'axis'`: without it echarts formats
       * the number itself, in English, whatever the language is. See
       * `utils/localeFormat`.
       */
      tooltip: {
        trigger: 'axis',
        valueFormatter: (v) => formatDecimal(Number(v), 1, lng),
      },
      legend: {
        right: 0,
        top: 'middle',
        orient: 'vertical',
        textStyle: { fontSize: 12 },
      },
      grid: {
        left: '3%',
        right: '20%',
        top: '10%',
        bottom: '5%',
        containLabel: true,
      },
      xAxis: {
        type: 'category',
        data: monthLabels,
        axisLabel: { fontSize: 12, color: chrome.muted },
      },
      yAxis: {
        type: 'value',
        // The unit lives on the summary rows, and the factor table holds more than
        // one (liter, kWh, kg). If the drawn fuels disagree the axis stays unitless
        // rather than labelling kWh bars as litres.
        name: axisUnit
          ? String(t('cpi.axis.fuelQuantityUnit', { unit: axisUnit }))
          : String(t('cpi.axis.fuelQuantity')),
        nameTextStyle: { color: chrome.muted, fontSize: 11 },
        axisLabel: { formatter: (v: number) => formatCompact(v, lng), color: chrome.muted },
      },
      series,
    };

    chartInstance.current.setOption(option, true);
  }, [pivotedData, chartNode, lng, monthNames, t, data]);

  useEffect(() => {
    const handleResize = () => chartInstance.current?.resize();
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      chartInstance.current?.dispose();
      chartInstance.current = null;
    };
  }, []);

  if (error && !isLoading) {
    return (
      <div className="fuel-consumption-chart">
        <h3 className="fuel-consumption-chart__title">{t('cpi.analytics.fuelQuantityByType')}</h3>
        <ChartErrorState onRetry={onRetry} />
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="fuel-consumption-chart fuel-consumption-chart--loading">
        <h3 className="fuel-consumption-chart__title">{t('cpi.analytics.fuelQuantityByType')}</h3>
        <div className="fuel-consumption-chart__skeleton" />
      </div>
    );
  }

  if (!data || data.monthly.length === 0) {
    return (
      <div className="fuel-consumption-chart">
        <h3 className="fuel-consumption-chart__title">{t('cpi.analytics.fuelQuantityByType')}</h3>
        <p className="chart-no-data">{t('cpi.noDataAvailableForChart')}</p>
      </div>
    );
  }

  return (
    <div className="fuel-consumption-chart">
      <h3 className="fuel-consumption-chart__title">{t('cpi.analytics.fuelQuantityByType')}</h3>
      <div ref={chartCallbackRef} style={{ height: '350px', width: '100%' }} />
    </div>
  );
};

export default FuelConsumptionChart;
